import React, { useCallback } from 'react';
import { Form, Upload, Button, notification, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { RcFile } from 'antd/es/upload';
import { ExpenseFile } from '../types';

interface FileUploadProps {
  index: number;
  recordId: string;
  headerId: string;
  token: string;
  files: ExpenseFile[];
  approving: boolean;
  filePath?: string;
  onFileChange: (id: string, index: number, file?: RcFile) => boolean;
  onDownload: (token: string, headerId: string, recordId: string) => void;
  disabled?: boolean;
}

/**
 * Accepted MIME types for file uploads
 * Includes common image formats and PDF for receipts and documents
 */
const ACCEPTED_FILE_TYPES = [
  'image/png',
  'image/jpeg', 
  'image/heic',
  'application/pdf',
] as const;

/**
 * Reusable File Upload Component
 * 
 * Handles file upload logic with validation and preview functionality.
 * Eliminates duplication between table and single row modes.
 */
export const FileUpload: React.FC<FileUploadProps> = ({
  index,
  recordId,
  headerId,
  token,
  files,
  approving,
  filePath = '',
  onFileChange,
  onDownload,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const handleBeforeUpload = useCallback((file: RcFile) => {
    const isValidType = ACCEPTED_FILE_TYPES.includes(
      file.type as typeof ACCEPTED_FILE_TYPES[number]
    );
    
    if (!isValidType) {
      notification.error({
        message: t('File is not a supported file format', {
          fileName: file.name,
        }),
        description: t(
          'Please upload a valid image (PNG, JPG, JPEG, GIF) or PDF'
        ),
        placement: 'topRight',
        duration: 4.5,
      });
      return Upload.LIST_IGNORE;
    }
    
    onFileChange(recordId, index, file);
    return false;
  }, [recordId, index, onFileChange, t]);

  const handleRemove = useCallback(() => {
    onFileChange(recordId, index);
  }, [recordId, index, onFileChange]);

  const handlePreview = useCallback(() => {
    onDownload(token, headerId, recordId);
  }, [token, headerId, recordId, onDownload]);

  const handleChange = useCallback((info: any) => {
    if (info.file.status === 'done') {
      message.success(`${info.file.name} file uploaded successfully`);
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} file upload failed.`);
    }
  }, []);

  return (
    <Form.Item
      name={['expenses', index, 'file_path']}
      rules={[
        () => ({
          validator(_, file) {
            if (
              approving &&
              filePath === '' &&
              (!file || file?.file?.status === 'removed')
            ) {
              return Promise.reject(new Error(t('Please upload a file!')));
            }
            return Promise.resolve();
          },
        }),
      ]}
    >
      <Upload
        name="file"
        beforeUpload={handleBeforeUpload}
        onRemove={handleRemove}
        onPreview={handlePreview}
        fileList={files[index]?.fileList}
        onChange={handleChange}
        multiple={false}
        maxCount={1}
        disabled={disabled}
      >
        <Button icon={<UploadOutlined />} disabled={disabled}>
          {t('Upload')}
        </Button>
      </Upload>
    </Form.Item>
  );
};